// The pivot scale of the mark scale (R1981, ui-map-interface.md §6.1).
//
// Three thin wrappers over the three sinks of `batches.ts`. Each one maps a
// point to `c + (p - c) * k` about one shared pivot `c`, and multiplies a
// width, a radius, a dash, a quad size and a sprite scale by `k`. A layer
// sets the pivot to one hull before it packs the marks of that hull, so a
// look recipe that draws through a sink of its context scales with the hull
// and needs no edit of its own.
//
// The wrappers are for a scale other than 1. At 1 the layer hands out the
// raw sink, because `c + (p - c) * 1` is not always `p` in floating point,
// and the season game must not move a pixel.

import type { CameraUniforms } from '../layer';
import type { CircleSink, QuadSink, SegmentSink, SpriteEntry } from './batches';

/** The one pivot and scale the three wrappers of a layer share. */
export interface Pivot {
  x: number;
  y: number;
  k: number;
}

/** A quad sink whose marks scale about the pivot. */
export class PivotQuads implements QuadSink {
  constructor(private readonly raw: QuadSink, private readonly p: Pivot) {}

  begin(): void { this.raw.begin(); }
  get count(): number { return this.raw.count; }
  get globalAlpha(): number { return this.raw.globalAlpha; }
  set globalAlpha(a: number) { this.raw.globalAlpha = a; }
  get stepScale(): Float32Array { return this.raw.stepScale; }

  quad(
    x: number, y: number, ox: number, oy: number, w: number, h: number,
    rot: number, flags: number, color: number, alpha: number,
    u0: number, v0: number, u1: number, v1: number,
  ): void {
    const p = this.p, k = p.k;
    this.raw.quad(p.x + (x - p.x) * k, p.y + (y - p.y) * k, ox * k, oy * k, w * k, h * k,
      rot, flags, color, alpha, u0, v0, u1, v1);
  }

  sprite(
    x: number, y: number, rot: number, entry: SpriteEntry,
    tint: number, alpha: number, scale = 1, flags = 0, centred = true,
  ): void {
    const p = this.p, k = p.k;
    this.raw.sprite(p.x + (x - p.x) * k, p.y + (y - p.y) * k, rot, entry,
      tint, alpha, scale * k, flags, centred);
  }

  glyph(
    px: number, py: number, x: number, y: number, rot: number, entry: SpriteEntry,
    tint: number, alpha: number, scale: number, flags: number,
  ): void {
    const p = this.p, k = p.k;
    this.raw.glyph(p.x + (px - p.x) * k, p.y + (py - p.y) * k,
      p.x + (x - p.x) * k, p.y + (y - p.y) * k, rot, entry, tint, alpha, scale * k, flags);
  }

  flush(cam: CameraUniforms): number { return this.raw.flush(cam); }
}

/** A segment sink whose lines scale about the pivot. */
export class PivotSegments implements SegmentSink {
  constructor(private readonly raw: SegmentSink, private readonly p: Pivot) {}

  begin(): void { this.raw.begin(); }
  get count(): number { return this.raw.count; }
  get globalAlpha(): number { return this.raw.globalAlpha; }
  set globalAlpha(a: number) { this.raw.globalAlpha = a; }

  line(
    x0: number, y0: number, x1: number, y1: number,
    width: number, color: number, alpha: number,
    flags?: number, dashOn?: number, dashOff?: number,
  ): void {
    const p = this.p, k = p.k;
    this.raw.line(p.x + (x0 - p.x) * k, p.y + (y0 - p.y) * k,
      p.x + (x1 - p.x) * k, p.y + (y1 - p.y) * k, width * k, color, alpha, flags,
      dashOn === undefined ? undefined : dashOn * k,
      dashOff === undefined ? undefined : dashOff * k);
  }

  flush(cam: CameraUniforms): number { return this.raw.flush(cam); }
}

/** A circle sink whose rings and discs scale about the pivot. */
export class PivotCircles implements CircleSink {
  constructor(private readonly raw: CircleSink, private readonly p: Pivot) {}

  begin(): void { this.raw.begin(); }
  get count(): number { return this.raw.count; }
  get globalAlpha(): number { return this.raw.globalAlpha; }
  set globalAlpha(a: number) { this.raw.globalAlpha = a; }

  ring(x: number, y: number, r: number, width: number, color: number, alpha: number, flags?: number): void {
    const p = this.p, k = p.k;
    this.raw.ring(p.x + (x - p.x) * k, p.y + (y - p.y) * k, r * k, width * k, color, alpha, flags);
  }

  disc(x: number, y: number, r: number, color: number, alpha: number, flags?: number): void {
    const p = this.p, k = p.k;
    this.raw.disc(p.x + (x - p.x) * k, p.y + (y - p.y) * k, r * k, color, alpha, flags);
  }

  arc(
    x: number, y: number, r: number, width: number,
    start: number, sweep: number, color: number, alpha: number, flags?: number,
  ): void {
    const p = this.p, k = p.k;
    this.raw.arc(p.x + (x - p.x) * k, p.y + (y - p.y) * k, r * k, width * k,
      start, sweep, color, alpha, flags);
  }

  dashed(
    x: number, y: number, r: number, width: number,
    count: number, fill: number, color: number, alpha: number,
    start?: number, sweep?: number, flags?: number,
  ): void {
    const p = this.p, k = p.k;
    this.raw.dashed(p.x + (x - p.x) * k, p.y + (y - p.y) * k, r * k, width * k,
      count, fill, color, alpha, start, sweep, flags);
  }

  flush(cam: CameraUniforms): number { return this.raw.flush(cam); }
}
